import { FormMetricsCommentItem } from 'services/models/form-builder/form-metrics/chart-data/comments-chart-data.model';
import { FormMetricsCategoryClickEvent } from 'services/models/form-builder/form-metrics/form-metrics-drilldown.model';
import { FormMetricsRespondentAnswerItemModel } from 'services/models/form-builder/form-metrics/form-metrics-drilldown.model';
import { FormMetricsChartType } from 'services/models/form-builder/form-metrics/form-metrics.enum';

const HIGHLIGHT_DIM_OPACITY = 0.15;
const HIGHLIGHT_STROKE_WIDTH = 2;

/** Mapa de contorno definido pelo UI/UX: cor base → cor de stroke. */
const HIGHLIGHT_STROKE_MAP: Record<string, string> = {
  '#7367F0': '#1507AC', // primary (roxo)
  '#28C76F': '#18854A', // success (verde)
  '#FF9F43': '#B16C2A', // warning (laranja)
};
const HIGHLIGHT_STROKE_FALLBACK_DARKEN = 0.4;

export interface FormMetricsSliceMeta {
  optionId: string | null;
  value?: string | number | null;
  label: string;
}

export function buildCategoryClickEvent(
  formItemId: string,
  chartType: FormMetricsChartType,
  slice: FormMetricsSliceMeta
): FormMetricsCategoryClickEvent {
  return {
    formItemId,
    chartType,
    optionId: slice.optionId,
    value: slice.value ?? null,
    label: slice.label,
  };
}

export function findHighlightForItem(
  answers: FormMetricsRespondentAnswerItemModel[] | null | undefined,
  formItemId: string
): FormMetricsRespondentAnswerItemModel | null {
  if (!answers?.length || !formItemId) {
    return null;
  }
  return answers.find(answer => answer.formItemId === formItemId) || null;
}

/** Extrai comentários do respondente a partir do payload de respondent-answers. */
export function commentsFromHighlightAnswers(
  answers: FormMetricsRespondentAnswerItemModel[] | null | undefined,
  formItemId: string
): FormMetricsCommentItem[] {
  if (!answers?.length || !formItemId) {
    return [];
  }

  return answers
    .filter(answer => answer.formItemId === formItemId && isCommentHighlightAnswer(answer))
    .map(answer => toCommentItem(answer))
    .filter(comment => !!comment.text);
}

function isCommentHighlightAnswer(answer: FormMetricsRespondentAnswerItemModel): boolean {
  if (answer.chartType !== FormMetricsChartType.COMMENT) {
    return false;
  }
  return !!(answer.value || '').trim();
}

function toCommentItem(answer: FormMetricsRespondentAnswerItemModel): FormMetricsCommentItem {
  return {
    answerId: '',
    text: (answer.value || '').trim(),
    submittedAt: '',
    submissionId: '',
  };
}

export function resolveHighlightedIndexes(
  slices: FormMetricsSliceMeta[],
  highlight: FormMetricsRespondentAnswerItemModel | null
): number[] {
  if (!highlight || !slices?.length) {
    return [];
  }

  const optionIds = new Set((highlight.optionIds || []).filter(Boolean));
  const highlightValue = highlight.value != null ? String(highlight.value) : null;
  const highlightLabels = new Set((highlight.labels || []).map(l => l?.toLowerCase()).filter(Boolean));

  const indexes: number[] = [];
  slices.forEach((slice, index) => {
    if (slice.optionId && optionIds.has(slice.optionId)) {
      indexes.push(index);
      return;
    }
    if (highlightValue != null && slice.value != null && String(slice.value) === highlightValue) {
      indexes.push(index);
      return;
    }
    if (slice.label && highlightLabels.has(slice.label.toLowerCase())) {
      indexes.push(index);
    }
  });

  return indexes;
}

/** Build stroke config to outline highlighted slices with a darker version of their own color. */
export function buildHighlightStroke(
  baseColors: string[],
  highlightedIndexes: number[]
): { width: number; colors: string[] } | null {
  if (!highlightedIndexes.length || !baseColors?.length) {
    return null;
  }

  const highlighted = new Set(highlightedIndexes);
  const colors = baseColors.map((color, i) =>
    highlighted.has(i) ? resolveStrokeColor(color) : 'transparent'
  );

  return { width: HIGHLIGHT_STROKE_WIDTH, colors };
}

/** Dim non-highlighted slices; leave all opaque when no highlight. */
export function applyHighlightColors(baseColors: string[], highlightedIndexes: number[]): string[] {
  if (!baseColors?.length) {
    return baseColors || [];
  }
  if (!highlightedIndexes.length) {
    return [...baseColors];
  }

  const highlighted = new Set(highlightedIndexes);
  return baseColors.map((color, index) =>
    highlighted.has(index) ? color : withOpacity(color, HIGHLIGHT_DIM_OPACITY)
  );
}

function withOpacity(color: string, opacity: number): string {
  if (!color) {
    return color;
  }
  if (color.startsWith('#') && (color.length === 7 || color.length === 4)) {
    const hex = color.length === 4
      ? `#${color[1]}${color[1]}${color[2]}${color[2]}${color[3]}${color[3]}`
      : color;
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${opacity})`;
  }
  return color;
}

/** Darken a hex color by a given factor (0 = no change, 1 = black). */
function darkenColor(color: string, factor: number): string {
  if (!color) {
    return color;
  }
  if (color.startsWith('#') && (color.length === 7 || color.length === 4)) {
    const hex = color.length === 4
      ? `#${color[1]}${color[1]}${color[2]}${color[2]}${color[3]}${color[3]}`
      : color;
    const r = Math.round(parseInt(hex.slice(1, 3), 16) * (1 - factor));
    const g = Math.round(parseInt(hex.slice(3, 5), 16) * (1 - factor));
    const b = Math.round(parseInt(hex.slice(5, 7), 16) * (1 - factor));
    return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
  }
  return color;
}

/** Resolve stroke color from UI/UX map or fallback to darkened version. */
function resolveStrokeColor(baseColor: string): string {
  const upper = (baseColor || '').toUpperCase();
  const mapped = HIGHLIGHT_STROKE_MAP[upper];
  if (mapped) {
    return mapped;
  }
  return darkenColor(baseColor, HIGHLIGHT_STROKE_FALLBACK_DARKEN);
}
